import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  ChevronLeft,
  Plus,
  ChevronRight,
  Copy,
  Globe,
  CalendarX,
} from "lucide-react";
import LeadCalendar from "./LeadCalendar";
import AddEditEvent from "./AddEditEvent";
import AttractiveEmptyState from "../../common/AttractiveEmptyState";

import { eventStyles, eventIcons } from "./calendarData";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllEvents,
  partialUpdateEvent,
} from "../../actionCreator/calendarActionCreator";
import { getPublicMeetingLinks } from "../../actionCreator/publicMeetingLinkActionCreator";
import { getMeetingTypes } from "../../actionCreator/meetingTypesActionCreator";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { EventStatus } from "../../config";
import { getUserInfo } from "../../utils/utils";
import { useAlert } from "../../context/AlertProvider";
import TopCalendarHeader from "./TopCalendarHeader";
import {
  Button,
  Calendar,
  Col,
  List,
  Row,
  Typography,
  Tooltip,
  Input,
} from "antd";
import { NavLink } from "react-router-dom";
import UpcomingEventsPanel from "./UpcomingEventsSidebar";
import { formatDueDate } from "../../utils/formatDate";

dayjs.extend(utc);
dayjs.extend(timezone);
const { Text } = Typography;

const LeadTimelineCalendar = () => {
  const calendarRef = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);
  const allEvents = useSelector((state) => state.calendarReducer.allEvents);
  const [isAddEditEvent, setAddEditEvent] = useState(false);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const userTz = dayjs.tz.guess();
  const date = searchParams.get("minDate");
  const { id: userId } = getUserInfo();
  const upcomingEvents = useSelector(
    (state) => state.calendarReducer.upcomingEvents,
  );
  const sortedEvents = [...upcomingEvents]
    .filter(
      (event) =>
        (dayjs(event.dueDate).isSame(dayjs(), "day") ||
          dayjs(event.dueDate).isAfter(dayjs())) &&
        event.status !== EventStatus.COMPLETED,
    )
    .sort((a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix())
    .slice(0, 7);

  const { publicMeetingLinks } = useSelector(
    (state) => state.publicMeetingLinkReducer,
  );
  const { meetingTypes } = useSelector((state) => state.meetingTypeReducer);

  useEffect(() => {
    dispatch(getPublicMeetingLinks());
    dispatch(getMeetingTypes());
  }, [dispatch]);

  const activeLink =
    publicMeetingLinks && publicMeetingLinks.length > 0
      ? publicMeetingLinks[0]
      : null;

  const savedCustomUrl = activeLink?.customUrl || "";
  const hasMeetingTypes = meetingTypes && meetingTypes.length > 0;
  const isLinkActive = activeLink
    ? activeLink.isActive !== false && hasMeetingTypes
    : false;
  const originURL = window.location.origin + "/schedule-meeting";
  const savedFullURL = savedCustomUrl ? `${originURL}/${savedCustomUrl}` : "";

  const handleCopyLink = () => {
    if (!savedFullURL) return;
    if (!hasMeetingTypes) {
      showAlert(
        "warning",
        <span>
          Please create at least one meeting type before sharing your booking
          link.{" "}
          <NavLink
            to="/leads-timeline/availability?tab=2"
            className="tw-text-theme tw-underline tw-font-semibold"
          >
            Click here to create a meeting type
          </NavLink>
        </span>,
      );
      return;
    }
    if (!isLinkActive) {
      showAlert(
        "warning",
        <span>
          Your booking link is not active. Please activate it before sharing.{" "}
          <NavLink
            to="/leads-timeline/availability?tab=5"
            className="tw-text-theme tw-underline tw-font-semibold"
          >
            Click here to activate
          </NavLink>
        </span>,
      );
      return;
    }
    navigator.clipboard.writeText(savedFullURL);
    showAlert("success", "Booking link copied!");
  };

  useEffect(() => {
    setEvents(allEvents);
  }, [allEvents]);

  useEffect(() => {
    setCurrentDate(dayjs(date));
  }, [date]);

  const onFilterChange = (key, val) => {
    if (!!val && val !== "all") searchParams.set(key, val);
    else searchParams.delete(key);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const changeView = (view) => {
    const api = calendarRef.current?.getApi();
    onFilterChange("calendarView", view);
    if (api) api.changeView(view);
  };

  useEffect(() => {
    if (!userId) return;
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }

    dispatch(
      getAllEvents({
        ...allParams,
        userId,
      }),
    ).then(async (res) => {
      if (!res?.length) {
        const type = await eventTypes?.find(
          (i) => i?.calendarEventTypeId === parseInt(allParams?.type),
        )?.typeName;

        if (allParams?.type && allParams?.keyword) {
          showAlert(
            "error",
            `No results found for event type ${type} and ${allParams?.keyword}.`,
          );
        } else if (allParams?.type && type) {
          showAlert("error", `No results found for event type ${type}.`);
        } else if (allParams?.keyword) {
          showAlert("error", `No results found for ${allParams?.keyword}.`);
        }
      }
    });
  }, [dispatch, searchParams, userId]);

  const handleEventClick = (info) => {
    const event = events.find(
      (e) => e.calendarEventId === info.calendarEventId,
    );

    const eventType = eventTypes?.find(
      (i) => i?.calendarEventTypeId === event?.calendarEventTypeId,
    )?.typeName;
    if (eventType) {
      setAddEditEvent(true);
    }
  };

  const handleEventDrop = async (info) => {
    const event = events.find(
      (e) => e.calendarEventId === parseInt(info?.event?.id),
    );

    const eventType = eventTypes?.find(
      (i) => i?.calendarEventTypeId === event?.calendarEventTypeId,
    )?.typeName;

    if (eventType) {
      await dispatch(
        partialUpdateEvent({
          calendarEventId: info?.event?.id,
          dueDate: dayjs(info.event.start),
        }),
      );
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }

      dispatch(
        getAllEvents({
          ...allParams,
          userId,
        }),
      );
      setEvents((prev) =>
        prev.map((e) => {
          return e.calendarEventId === parseInt(info?.event?.id)
            ? {
                ...e,
                dueDate: dayjs(info.event.start).format("YYYY-MM-DD HH:mm"),
              }
            : e;
        }),
      );
    } else info.revert();
  };

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;

    const timer = setTimeout(() => {
      api.updateSize();
    }, 300);

    return () => clearTimeout(timer);
  }, [isSidebarOpen]);

  const calendarEvents = events
    .filter((e) => e.status !== EventStatus.COMPLETED)
    .map((e) => {
      const type = eventTypes?.find(
        (d) => d?.calendarEventTypeId === e.calendarEventTypeId,
      )?.typeName;

      return {
        id: e.calendarEventId,
        start: e.dueDate,
        extendedProps: {
          ...e,
          type,
        },
        editable: !!type && e?.status !== EventStatus.COMPLETED,
      };
    });

  const goPrev = () => {
    const api = calendarRef.current.getApi();
    api.prev();
    const date = dayjs(api.getDate());
    setCurrentDate(date);
    onFilterChange(
      "minDate",
      dayjs(date).tz(userTz).startOf("month").toISOString(),
    );
    onFilterChange(
      "maxDate",
      dayjs(date).tz(userTz).endOf("month").toISOString(),
    );
  };

  const goNext = () => {
    const api = calendarRef.current.getApi();
    api.next();
    const date = dayjs(api.getDate());
    setCurrentDate(date);
    onFilterChange(
      "minDate",
      dayjs(date).tz(userTz).startOf("month").toISOString(),
    );
    onFilterChange(
      "maxDate",
      dayjs(date).tz(userTz).endOf("month").toISOString(),
    );
  };

  const goToday = () => {
    const api = calendarRef.current.getApi();
    api.today();
    const date = dayjs(api.getDate());
    setCurrentDate(date);
    onFilterChange(
      "minDate",
      dayjs(date).tz(userTz).startOf("month").toISOString(),
    );
    onFilterChange(
      "maxDate",
      dayjs(date).tz(userTz).endOf("month").toISOString(),
    );
  };

  const handleDateChange = (monthIndex) => {
    setCurrentDate(monthIndex);
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(
      dayjs(monthIndex).tz(userTz).startOf("day").toISOString(),
    );
    onFilterChange(
      "minDate",
      dayjs(monthIndex).tz(userTz).startOf("month").toISOString(),
    );
    onFilterChange(
      "maxDate",
      dayjs(monthIndex).tz(userTz).endOf("month").toISOString(),
    );
  };

  const headerRender = ({ value, onChange }) => {
    const current = value;

    const goPrev = () => {
      const prevMonth = current.subtract(1, "month");
      onChange(prevMonth);
    };

    const goNext = () => {
      const nextMonth = current.add(1, "month");
      onChange(nextMonth);
    };

    return (
      <div className="tw-flex tw-justify-between tw-items-center tw-px-1 tw-pb-3 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100/80 tw-mb-2">
        <span className="tw-text-xs tw-font-bold tw-text-slate-700">
          {current.format("MMMM YYYY")}
        </span>

        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            onClick={goPrev}
            type="button"
            className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-transition-all tw-duration-150"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={goNext}
            type="button"
            className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-transition-all tw-duration-150"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <TopCalendarHeader
        currentDate={currentDate}
        goPrev={goPrev}
        goNext={goNext}
        goToday={goToday}
        handleDateChange={handleDateChange}
        searchParams={searchParams}
        onFilterChange={onFilterChange}
        openAddEvent={() => setAddEditEvent(true)}
        changeView={changeView}
        isSidebarOpen={isSidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <Row
        gutter={[24, 24]}
        className="tw-h-full tw-w-full tw-flex tw-justify-between"
      >
        <Col
          xs={isSidebarOpen ? 24 : 0}
          lg={isSidebarOpen ? 6 : 0}
          className="tw-transition-all tw-duration-300 tw-ease-in-out tw-overflow-hidden"
        >
          {isSidebarOpen && (
            <div>
              <>
                <div className="tw-flex tw-justify-between tw-items-center tw-my-4">
                  <Button
                    type="primary"
                    size="large"
                    className="theme-btn tw-font-bold !tw-h-[42px] !tw-rounded-xl tw-text-xs tw-w-full tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] tw-transition-all tw-duration-200"
                    onClick={() => setAddEditEvent(true)}
                  >
                    <Plus size={16} className="tw-mr-1.5 tw-inline-block" /> Create Event
                  </Button>
                </div>
                <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-rounded-2xl tw-p-4 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)] tw-mb-6 navigation-calendar">
                  <Calendar
                    value={
                      currentDate.format("MMMM YYYY") === "Invalid Date"
                        ? dayjs()
                        : currentDate
                    }
                    fullscreen={false}
                    headerRender={headerRender}
                    onPanelChange={(value, mode) => {
                      handleDateChange(value);
                    }}
                    onSelect={(value) => value && handleDateChange(value)}
                    onChange={(value) => value && handleDateChange(value)}
                  />
                </div>

                {/* Meeting Booking Link Card */}
                <div className="tw-mb-6 tw-p-5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
                  <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                    <span className="tw-font-bold tw-text-[11px] tw-text-slate-400 tw-uppercase tw-tracking-wider">
                      My Booking Link
                    </span>
                    {!isLinkActive && (
                      <span className="tw-text-[10px] tw-font-bold tw-text-red-500 tw-bg-red-50 tw-px-2 tw-py-0.5 tw-rounded-md">
                        Inactive
                      </span>
                    )}
                    {savedCustomUrl && (
                      <Tooltip title="Open Live Booking Page">
                        <a
                          href={savedFullURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tw-text-[#5D44FF] hover:tw-opacity-80 tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-md hover:tw-bg-indigo-50/50 tw-transition-colors"
                        >
                          <Globe size={16} />
                        </a>
                      </Tooltip>
                    )}
                  </div>
                  {savedCustomUrl ? (
                    <>
                      <div className="tw-flex tw-gap-2">
                        <Input
                          value={savedFullURL}
                          readOnly
                          className="!tw-bg-slate-50/50 tw-text-xs tw-text-slate-500 tw-rounded-lg tw-h-9 tw-border-slate-200"
                        />
                        <Button
                          icon={<Copy size={16} />}
                          onClick={handleCopyLink}
                          className="tw-flex tw-items-center tw-justify-center tw-h-9 tw-w-9 !tw-rounded-lg !tw-border-slate-200 hover:!tw-border-slate-350"
                        />
                      </div>
                      {!isLinkActive && (
                        <div className="tw-mt-3 tw-text-[11px] tw-text-slate-500 tw-leading-relaxed">
                          {!hasMeetingTypes ? (
                            <>
                              <span>
                                At least one meeting type is required to
                                activate your link.{" "}
                              </span>
                              <NavLink
                                to="/leads-timeline/availability?tab=2"
                                className="tw-text-[#5D44FF] tw-font-bold hover:tw-underline"
                              >
                                Create now
                              </NavLink>
                            </>
                          ) : (
                            <>
                              <span>Your booking link is not active. </span>
                              <NavLink
                                to="/leads-timeline/availability?tab=5"
                                className="tw-text-[#5D44FF] tw-font-bold hover:tw-underline"
                              >
                                Activate link
                              </NavLink>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="tw-text-xs tw-text-slate-500 tw-flex tw-flex-col tw-gap-2">
                      <span>No booking link configured yet.</span>
                      <NavLink
                        to="/leads-timeline/availability"
                        className="tw-text-[#5D44FF] tw-font-semibold hover:tw-underline"
                      >
                        Set up now
                      </NavLink>
                    </div>
                  )}
                </div>
              </>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-px-1">
                <div className="tw-font-bold tw-text-slate-700 tw-text-sm">
                  Upcoming Events
                </div>
                <UpcomingEventsPanel />
              </div>
              <List
                dataSource={sortedEvents}
                className={`tw-mb-6 tw-mt-2 tw-max-h-[200px] ${sortedEvents.length > 0 ? "tw-overflow-y-auto" : "tw-overflow-visible"}`}
                locale={{
                  emptyText: (
                    <AttractiveEmptyState
                      icon={CalendarX}
                      title="No events scheduled today"
                      description="Your schedule is completely clear for today."
                    />
                  ),
                }}
                renderItem={(event) => {
                  const type = eventTypes?.find(
                    (i) =>
                      i?.calendarEventTypeId === event?.calendarEventTypeId,
                  )?.typeName;

                  return (
                    <List.Item
                      key={event.id}
                      className="!tw-py-2.5 !tw-px-3 tw-rounded-xl tw-border tw-border-solid tw-border-slate-50 hover:tw-bg-slate-50/50 tw-transition-colors tw-mb-1.5 last:tw-mb-0"
                    >
                      <List.Item.Meta
                        title={
                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <span className="tw-text-slate-400 tw-text-sm">
                              {eventIcons[type] || <Plus size={14} />}
                            </span>
                            <span className="tw-text-xs tw-font-bold tw-text-slate-700">
                              {event.title}
                            </span>
                          </div>
                        }
                        description={
                          <span className="tw-text-[11px] tw-text-slate-400 tw-block tw-mt-0.5">
                            {formatDueDate(event.dueDate)}
                          </span>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          )}
        </Col>
        <Col
          xs={24}
          lg={isSidebarOpen ? 18 : 24}
          className={`tw-w-full tw-mt-4 tw-transition-all tw-duration-300 tw-ease-in-out ${
            isSidebarOpen ? "tw-translate-x-0" : "tw-translate-x-0"
          }`}
        >
          <div className="tw-relative">
            {!isSidebarOpen && (
              <Button
                size="large"
                className="!tw-bg-theme hover:!tw-bg-theme active:!tw-bg-theme !tw-border-theme hover:!tw-border-theme !tw-text-white hover:!tw-text-white focus:!tw-text-white tw-shadow-[0_10px_20px_rgba(93,68,255,0.30)] hover:tw-shadow-[0_10px_20px_rgba(93,68,255,0.30)] tw-transition-all tw-duration-200 tw-absolute tw-left-3 tw-top-3 tw-z-20 tw-size-[52px] tw-rounded-[14px]"
                onClick={() => setAddEditEvent(true)}
              >
                <Plus size={24} />
              </Button>
            )}
            <LeadCalendar
              key={isSidebarOpen ? "sidebar-open" : "sidebar-closed"}
              selectedDate={currentDate}
              calendarRef={calendarRef}
              calendarEvents={calendarEvents}
              handleEventClick={handleEventClick}
              handleEventDrop={handleEventDrop}
              eventStyles={eventStyles}
              eventIcons={eventIcons}
              events={events}
              onCreate={(date) => {
                setAddEditEvent(true);
                setSelectedDate(date);
              }}
            />
          </div>
        </Col>
      </Row>

      <AddEditEvent
        isOpen={isAddEditEvent}
        handleCancel={() => setAddEditEvent(null)}
        selectedEvent={null}
        selectedDate={selectedDate}
      />
    </>
  );
};

export default LeadTimelineCalendar;
